import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";

import ProductLineProductPreviews from "./ProductLineProductPreviews.graphql";
import { productSearch } from "../../modules/api";

const PAGE_SIZE = 100;

interface IProductLineProductPreviews {
    searchProducts: {
        results: {
            nodes: Array<{ id: string, preview?: string }>
        }
    }
}

/**
 * Finds the first product picture in a brand's product line, read from the
 * search index `preview`. `brandID` must be the brand's ObjectId (matches the
 * index `brand.brandID`); `productLineKey` is the `ProductLine.key` carried by
 * products in the product-line parameter. Only the first page of results is
 * scanned. Resolves to `undefined` when no product has a picture.
 */
export default function useProductLinePreviewFinder() {
    const [execute] = useLazyQuery<IProductLineProductPreviews>(
        gql(ProductLineProductPreviews),
        { client: productSearch },
    );

    return async function findPreview(brandID: string, productLineKey: string) {
        const result = await execute({
            variables: {
                filter: {
                    brandIDs: [brandID],
                    productLineIDs: [productLineKey],
                },
                page: { first: PAGE_SIZE },
            },
        });

        return result.data?.searchProducts.results.nodes
            .find((node) => node.preview)
            ?.preview;
    };
}
